// Trainer-code RPCs — trusted wrappers.
// Supabase owns validation, discount math, and attribution. React
// never computes discount percentages, amounts, or eligibility.

import { supabase, supabaseConfigured } from './supabase';

function logSanitized(label, err) {
  try {
    console.warn('[etff:trainer]', label, {
      name: err?.name || null,
      code: err?.code || null,
      message: err?.message || null,
    });
  } catch { /* ignore */ }
}

// validate_trainer_code(p_code) — checks that a code is currently
// active, returns the trainer's display name and any trusted benefit
// information for display only.
export async function rpcValidateTrainerCode(code) {
  if (!supabaseConfigured || !supabase) {
    return { ok: false, data: null, error: 'Trainer codes are temporarily unavailable.' };
  }
  const p_code = typeof code === 'string' ? code.trim() : '';
  if (!p_code) {
    return { ok: false, data: null, error: 'Please enter a trainer code.' };
  }
  try {
    const { data, error } = await supabase.rpc('validate_trainer_code', { p_code });
    if (error) {
      logSanitized('validate_trainer_code:error', error);
      return { ok: false, data: null, error: "We couldn't find an active trainer with that code." };
    }
    return { ok: true, data, error: null };
  } catch (err) {
    logSanitized('validate_trainer_code:threw', err);
    return { ok: false, data: null, error: "We couldn't find an active trainer with that code." };
  }
}

// apply_trainer_attribution(p_code) — persists the customer's chosen
// trainer attribution against their current order context.
export async function rpcApplyTrainerAttribution(code) {
  if (!supabaseConfigured || !supabase) {
    return { ok: false, data: null, error: 'Trainer codes are temporarily unavailable.' };
  }
  const p_code = typeof code === 'string' ? code.trim() : '';
  if (!p_code) {
    return { ok: false, data: null, error: 'Please enter a trainer code.' };
  }
  try {
    const { data, error } = await supabase.rpc('apply_trainer_attribution', { p_code });
    if (error) {
      logSanitized('apply_trainer_attribution:error', error);
      return { ok: false, data: null, error: "We couldn't apply that trainer code. Please try again." };
    }
    return { ok: true, data, error: null };
  } catch (err) {
    logSanitized('apply_trainer_attribution:threw', err);
    return { ok: false, data: null, error: "We couldn't apply that trainer code. Please try again." };
  }
}

// clear_trainer_attribution() — removes any current trainer attribution.
export async function rpcClearTrainerAttribution() {
  if (!supabaseConfigured || !supabase) {
    return { ok: false, data: null, error: 'Trainer codes are temporarily unavailable.' };
  }
  try {
    const { data, error } = await supabase.rpc('clear_trainer_attribution');
    if (error) {
      logSanitized('clear_trainer_attribution:error', error);
      return { ok: false, data: null, error: "We couldn't remove the trainer code. Please try again." };
    }
    return { ok: true, data, error: null };
  } catch (err) {
    logSanitized('clear_trainer_attribution:threw', err);
    return { ok: false, data: null, error: "We couldn't remove the trainer code. Please try again." };
  }
}

// Tolerant readers.
export function readTrainerValidateOutcome(payload) {
  if (!payload || typeof payload !== 'object') return null;
  const o = payload.outcome;
  return typeof o === 'string' ? o : null;
}
export function readTrainerName(payload) {
  if (!payload || typeof payload !== 'object') return null;
  for (const k of ['trainer_name', 'display_name', 'name']) {
    const v = payload[k];
    if (typeof v === 'string' && v.length) return v;
  }
  return null;
}
// Free-form display text the backend chooses to expose (e.g.
// "5% off your order" or "Trainer credit applied"). React does not
// compute this; it just renders whatever the RPC returned.
export function readTrainerBenefitText(payload) {
  if (!payload || typeof payload !== 'object') return null;
  for (const k of ['benefit_text', 'display_benefit', 'discount_display', 'attribution_display']) {
    const v = payload[k];
    if (typeof v === 'string' && v.length) return v;
  }
  return null;
}
export function readAppliedTrainerCode(payload) {
  if (!payload || typeof payload !== 'object') return null;
  for (const k of ['applied_code', 'trainer_code', 'code']) {
    const v = payload[k];
    if (typeof v === 'string' && v.length) return v;
  }
  return null;
}
